"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

type Theme = "light" | "dark";

const STORAGE_KEY = "signpost-theme";

function readStoredTheme(): Theme {
    if (typeof window === "undefined") return "light";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "dark" ? "dark" : "light";
}

function applyTheme(theme: Theme) {
    document.documentElement.setAttribute("data-theme", theme);
}

export function ThemeToggle() {
    const pathname = usePathname() ?? "/";
    // Initialize from the DOM attribute set by the bootstrap script in
    // layout.tsx — that way SSR and the first paint agree, and we don't
    // briefly render the wrong icon.
    const [theme, setTheme] = useState<Theme>("light");
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const current = (document.documentElement.getAttribute("data-theme") as Theme | null) ?? readStoredTheme();
        setTheme(current);
        setMounted(true);
    }, []);

    function toggle() {
        const next: Theme = theme === "dark" ? "light" : "dark";
        setTheme(next);
        applyTheme(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* localStorage may be unavailable (private mode); ignore. */
        }
    }

    if (pathname.startsWith("/embed")) return null;

    const isDark = theme === "dark";
    const label = isDark ? "Switch to light mode" : "Switch to dark mode";

    return (
        <button
            type="button"
            className="theme-toggle"
            onClick={toggle}
            aria-label={label}
            title={label}
            // Hide until mounted so the icon doesn't flash the wrong glyph if
            // the bootstrap script hasn't run yet (e.g. SSR hydration).
            style={mounted ? undefined : { visibility: "hidden" }}
        >
            {isDark ? (
                /* Sun — shown in dark mode (click to go light) */
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
            ) : (
                /* Moon — shown in light mode (click to go dark) */
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
            )}
        </button>
    );
}
